import { clampUInt } from "../../../Common/IntRangeUtils";
import { isBrowser } from "../Helpers/BrowserHelpers";
import { featureFlagQueryParamPrefix } from "../Forms/Types/FeatureFlags";
import { ensureArrayHasLength } from "../../../Common/TypingUtils";

export function delay(timeout: number): Promise<void> {
    return new Promise(resolve => {
        setTimeout(resolve, timeout);
    });
}

export function declint(value: number, titles: [string, string, string]): string {
    const numberFrom5to19 = value % 100 > 4 && value % 100 < 20;

    if (numberFrom5to19) {
        return titles[2].replace("%s", value.toString());
    }
    const cases = [2, 0, 1, 1, 1, 2] as const;
    const pluralIndex = cases[clampUInt(value % 10, 0, 5)];

    return titles[pluralIndex].replace("%s", value.toString());
}

let ieVersion: undefined | false | number;

/**
 * returns version of IE or false, if browser is not Internet Explorer
 */
export function detectIE(): false | number {
    if (!isBrowser) {
        return false;
    }
    if (ieVersion != undefined) {
        return ieVersion;
    }

    if (!navigator) {
        ieVersion = false;
        return ieVersion;
    }

    const userAgent = navigator.userAgent;
    const msie = userAgent.indexOf("MSIE ");

    if (msie > 0) {
        // IE 10 or older => return version number
        ieVersion = parseInt(userAgent.substring(msie + 5, userAgent.indexOf(".", msie)), 10);

        return ieVersion;
    }

    const trident = userAgent.indexOf("Trident/");

    if (trident > 0) {
        // IE 11 => return version number
        const rv = userAgent.indexOf("rv:");

        ieVersion = parseInt(userAgent.substring(rv + 3, userAgent.indexOf(".", rv)), 10);

        return ieVersion;
    }

    const edge = userAgent.indexOf("Edge/");

    if (edge > 0) {
        // Edge (IE 12+) => return version number
        ieVersion = parseInt(userAgent.substring(edge + 5, userAgent.indexOf(".", edge)), 10);

        return ieVersion;
    }

    // other browser
    ieVersion = false;

    return ieVersion;
}

interface IParseQueryParams {
    queryParams: { [key: string]: string };
    featureFlags: { [key: string]: string };
}

export function parseQueryParams(): IParseQueryParams {
    const params = new URLSearchParams(window.location.search);
    const queryParams: Record<string, string> = {};
    const featureFlags: Record<string, string> = {};
    for (const [key, value] of params.entries()) {
        if (key === "gfv") {
            continue;
        }
        const decodedKey = decodeURIComponent(key);
        const decodedValue = decodeURIComponent(value);
        if (decodedKey.startsWith(featureFlagQueryParamPrefix)) {
            featureFlags[decodedKey.replace(featureFlagQueryParamPrefix, "")] = decodedValue;
        } else {
            if (decodedKey.includes(".") && decodedKey.split(".").length == 2) {
                applyToSecondDepth<typeof decodedValue, typeof queryParams>(queryParams, decodedKey, decodedValue);
            } else {
                queryParams[decodedKey] = decodedValue;
            }
        }
    }
    return { queryParams: queryParams, featureFlags: featureFlags };
}

/** Разбиравем sendToSignButtonOptions.visible=true => {sendToSignButtonOptions : {visible: true}}
 * Используется только для тестов, делать глубюже 2 - не вижу смысла, тк сильно усложняется алгоритм, да и не нужно
 * */
function applyToSecondDepth<T, U>(queryParams: U, key: string, value: T) {
    const splitByDot = key.split(".");
    ensureArrayHasLength(splitByDot, 2);
    const [first, second] = splitByDot;

    const result = { [second]: value };
    // @ts-ignore
    const existDepth = queryParams[first];
    if (existDepth) {
        Object.assign(existDepth, result);
    } else {
        // @ts-ignore
        queryParams[first] = result;
    }
}

export function smoothScrollBy(top: number = 0, left: number = 0): void {
    if (detectIE() === 8) {
        window.scrollBy(left, top);
    } else {
        window.scrollBy({
            top: top,
            left: left,
            behavior: "smooth",
        });
    }
}

export function smoothScroll(top: number = 0, left: number = 0): void {
    window.scroll({
        top: top,
        left: left,
        behavior: "smooth",
    });
}

export function scrollElementInCenter(element: Element): void {
    const { top, bottom } = element.getBoundingClientRect();

    if (bottom > document.documentElement.clientHeight) {
        smoothScrollBy(bottom - document.documentElement.clientHeight / 2);
    }

    if (top < 0) {
        smoothScrollBy(top - document.documentElement.clientHeight / 2);
    }
}

export function isTestEnvironment() {
    return window.location.hostname.includes(".testkontur.ru");
}

export const getElementWidthWithMargin = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);

    return element.offsetWidth + parseFloat(style.marginLeft) + parseFloat(style.marginRight);
};

export const getElementContentWidth = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);

    return element.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
};
